// Klaviyo onsite JavaScript API
// https://developers.klaviyo.com/en/docs/introduction_to_the_klaviyo_object
//
// gatsby-plugin-klaviyo only injects the async klaviyo.js script, so
// `window.klaviyo` doesn't exist until it downloads. We inject the official
// onsite stub in gatsby-ssr, which makes `window.klaviyo` a Proxy immediately
// and queues calls onto `window._klOnsite`; the real library drains that queue
// once it loads.
//
// We use the queue form `klaviyo.push(["method", ...params, callback])`. The
// callback fires once Klaviyo actually processes the call, so we wrap it in a
// Promise that callers can await (e.g. before navigating away). A timeout
// guarantees we never hang if the library is blocked (ad blockers) or slow.

import { isTrackerOn } from "./activeTrackers";
import { logTrackingDebug } from "./debug/debugLog";

type KlaviyoQueue = { push: (args: unknown[]) => void };

const CALLBACK_TIMEOUT_MS = 3000;

function getKlaviyo(): KlaviyoQueue | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  return (window as unknown as { klaviyo?: KlaviyoQueue }).klaviyo;
}

function push(method: string, ...params: unknown[]): Promise<unknown> {
  // Off in src/trackers.ts: resolve at once. The onsite stub still exists, so
  // pushing would make awaiting callers sit out the full callback timeout.
  if (!isTrackerOn("klaviyo")) {
    return Promise.resolve();
  }
  console.log(`Klaviyo push: ${method}`, ...params);
  logTrackingDebug("klaviyo", method === "track" ? `track ${params[0]}` : method);
  const klaviyo = getKlaviyo();
  if (!klaviyo) {
    console.error("Klaviyo is not available. Ensure the Klaviyo script is loaded.");
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let settled = false;
    const done = (result?: unknown) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };

    const timeout = setTimeout(() => done(), CALLBACK_TIMEOUT_MS);

    klaviyo.push([
      method,
      ...params,
      (result: unknown) => {
        console.log(`Klaviyo push callback: ${method}`, result);
        clearTimeout(timeout);
        done(result);
      },
    ]);
  });
}

/**
 * Identify the current visitor so that subsequent events attach to their
 * Klaviyo profile. Identity persists in the browser, so this only needs to be
 * called once (as soon as the email is known).
 */
export function identifyUser(properties: { email: string } & Record<string, unknown>) {
  return push("identify", properties);
}

export function trackEvent(event: string, properties: Record<string, unknown> = {}) {
  return push("track", event, properties);
}
